import { useEffect, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "@emotion/styled";
import { Tab, Tabs } from "@avenue-8/sidekick-ui";
import { ContactBriefingHeader } from "./components/ContactBriefingHeader";
import { SidekickUpdatesCards } from "./components/SidekickUpdatesCards";
import { RecentActivityBlock } from "./components/RecentActivityBlock";
import { CrmRelationshipOverview } from "./components/CrmRelationshipOverview";
import { RadarSignalsTab } from "./components/RadarSignalsTab/RadarSignalsTab";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import {
  CONTACT_BRIEFING_TAB_PARAM_KEY as TAB_PARAM_KEY,
  CONTACT_BRIEFING_RADAR_TAB_PARAM_VALUE as RADAR_TAB_PARAM_VALUE,
} from "@shared/utils/contact-briefing-links";
import type { ContactBriefing } from "./contacts.types";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 100%;
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
`;

const TabPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const TabPanel = ({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) =>
  active ? <TabPanelContent role="tabpanel">{children}</TabPanelContent> : null;

type ContactBriefingViewProps = {
  briefing: ContactBriefing;
};

export const ContactBriefingView = (props: ContactBriefingViewProps) => {
  const { briefing } = props;
  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const [searchParams, setSearchParams] = useSearchParams();

  const isMonitored = briefing.contact.isMonitoredSince !== null;

  const requestedRadarTab =
    searchParams.get(TAB_PARAM_KEY) === RADAR_TAB_PARAM_VALUE;
  const activeTab = requestedRadarTab && isMonitored ? 1 : 0;

  const shouldClearRadarTabParam = requestedRadarTab && !isMonitored;

  useEffect(() => {
    if (!shouldClearRadarTabParam) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(TAB_PARAM_KEY);
        return next;
      },
      { replace: true },
    );
  }, [shouldClearRadarTabParam, setSearchParams]);

  const handleTabChange = (_: unknown, value: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === 1) {
          next.set(TAB_PARAM_KEY, RADAR_TAB_PARAM_VALUE);
        } else {
          next.delete(TAB_PARAM_KEY);
        }
        return next;
      },
      { replace: true },
    );
  };

  const crmBriefingContent = (
    <>
      <SidekickUpdatesCards contact={briefing.contact} />
      <RecentActivityBlock sections={briefing.recentActivity} />
      <CrmRelationshipOverview
        key={briefing.contact.id}
        contact={briefing.contact}
      />
    </>
  );

  return (
    <Container>
      <ContactBriefingHeader
        header={briefing.header}
        contact={briefing.contact}
      />
      {hasRadarModule ? (
        <>
          <TabsWrapper>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="CRM Briefing" />
              <Tab label={`Radar Signals`} disabled={!isMonitored} />
            </Tabs>
          </TabsWrapper>
          <TabPanel active={activeTab === 0}>{crmBriefingContent}</TabPanel>
          <TabPanel active={activeTab === 1 && isMonitored}>
            <RadarSignalsTab
              contactId={briefing.contact.id}
              contactName={briefing.header.name}
              isMonitoredSince={briefing.contact.isMonitoredSince}
            />
          </TabPanel>
        </>
      ) : (
        crmBriefingContent
      )}
    </Container>
  );
};
